import { useMemo, useRef, useState } from 'react'

import { useQuery } from '@apollo/client'
import * as Sentry from '@sentry/nextjs'
import {
  IconAlertTriangle,
  IconDots,
  IconStack,
  IconUsers,
} from '@tabler/icons-react'
import { Form, FormInstance, message, Popconfirm } from 'antd'
import { isEmpty } from 'lodash'
import moment, { Moment } from 'moment'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'

import { useProject } from '../../context/projectContext'
import { GET_PROJECT_LOCATIONS } from '../projects/graphQL'
import { GET_DEFECT_SETTINGS } from '../settings/graphQL'

import { DEFECT_PRIORITIES, DEFECT_STATUSES, DEFECT_TYPES } from './constants'
import { getAISelectAssignees } from './getAiSelectAssignees'
import { GET_ALL_ASSIGNEES } from './graphQL'

import { AudioTranscriber } from '@/components/ai/AudioTranscriber'
import {
  AssigneeInfo,
  AssigneeSelect,
} from '@/components/form-components/AssigneeSelect'
import { BaseButton } from '@/components/form-components/dropdowns/BaseButton'
import { DateDropdown } from '@/components/form-components/dropdowns/DateDropdown'
import { KeyValueDropdown } from '@/components/form-components/dropdowns/KeyValueDropdown'
import { ProjectLocationDropdown } from '@/components/form-components/dropdowns/ProjectLocation/ProjectLocationDropdown'
import { AssigneePill } from '@/components/shared-components/AssigneePill'
import { Alert } from '@/components/shared-components/BUI/Alert'
import { Input, TextArea } from '@/components/shared-components/BUI/Input'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { Text } from '@/components/shared-components/BUI/Text'
import { Uploader } from '@/components/shared-components/BUI/Uploader'
import { DozerLoading } from '@/components/shared-components/DozerLoading'
import { useAIActivity } from '@/context/aiActivityContext'
import { useLocale } from '@/context/localeContext'
import { FileUploadResult } from '@/hooks/useAttachmentUpload'
import { useEnabledModules } from '@/hooks/useEnabledModules'
import { useRegisterAIToolUsage } from '@/hooks/useRegisterAIToolUsage'
import { pluralise } from '@/utils/pluralise'
import { routes } from '@/utils/routes'

import { VoiceDefectHandlerResponse } from 'src/pages/api/ai/voiceDefects'
import { buildPassTheme } from 'src/styles/theme'

import {
  AssigneeType,
  AttachmentType,
  DefectAssigneesQuery,
  DefectAssigneesQueryVariables,
  DefectPriority,
  DefectSettingsUiQuery,
  DefectSettingsUiQueryVariables,
  DefectStatus,
  ProjectLocationsUiQuery,
  ProjectLocationsUiQueryVariables,
  Resource,
} from 'types/api.graphql'

export enum Field {
  PROJECT_ID = 'projectId',
  TITLE = 'title',
  DESCRIPTION = 'description',
  TYPE = 'type',
  STATUS = 'status',
  LOCATION = 'location',
  PRIORITY = 'priority',
  DUE_DATE = 'dueDate',
  ASSIGNEES = 'assignees',
  ATTACHMENTS = 'attachments',
  CONTINUE = 'continue',
  ID = 'id',
  TRANSCRIPTIONS = 'transcriptions',
}

export type FormFieldValues = {
  [Key in Field]: Key extends Field.ASSIGNEES
    ? AssigneeInfo[]
    : Key extends Field.ATTACHMENTS
      ? FileUploadResult[]
      : Key extends Field.STATUS
        ? DefectStatus
        : Key extends Field.PRIORITY
          ? DefectPriority
          : Key extends Field.CONTINUE
            ? boolean
            : Key extends Field.DUE_DATE
              ? string | Moment
              : string
}

const UPLOAD_WARNING = 'Please wait until all images have been uploaded'

const PendingBar = styled.div`
  ${({ theme }) => theme.font.label02}
  position: absolute;
  bottom: calc(100% + 0.5rem);
  left: 0%;
  width: 100%;
  background-color: #e0e0e0;
  padding: 1.25rem 1.875rem;
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const LoadingWrapper = styled.div`
  min-height: 200px;
`

const Content = styled.div`
  padding: 2.125rem 1.625rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Buttons = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`

const AssigneesWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Assignees = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`

const TitleInput = styled(TextArea).attrs({ naked: true })`
  ${({ theme }) => theme.font.heading02}
  color: ${({ theme }) => theme.colour.secondary._800};

  &::placeholder {
    color: ${({ theme }) => theme.colour.secondary._400};
  }
`

const DescriptionInput = styled(TextArea).attrs({ naked: true })`
  ${({ theme }) => theme.font.body02}
  color: ${({ theme }) => theme.colour.secondary._400};

  &::placeholder {
    color: ${({ theme }) => theme.colour.secondary._400};
  }
`

const isValidLocation = (
  locations: ProjectLocationsUiQuery['projectLocations'],
  locationId: string
) => {
  return locations.some((location) => {
    if (location.id === locationId) {
      return true
    }
    if (location.children && location.children.length > 0) {
      return isValidLocation(location.children, locationId)
    }
    return false
  })
}

const mapWorkers = (workers: DefectAssigneesQuery['workersByProject']) =>
  workers.map((worker) => ({
    type: AssigneeType.Worker,
    id: worker.id,
    name: `${worker.user.firstName} ${worker.user.lastName}`,
  }))

const mapSubcontractors = (
  subcontractors: DefectAssigneesQuery['subcontractorsByProject']
) =>
  subcontractors.map((subcontractor) => ({
    type: AssigneeType.Subcontractor,
    id: subcontractor.id,
    name: subcontractor.company.name,
  }))

const mapAdminUsers = (adminUsers: DefectAssigneesQuery['adminUsers']) =>
  adminUsers.map((adminUser) => ({
    type: AssigneeType.AdminUser,
    id: adminUser.id,
    name: `${adminUser.user.firstName} ${adminUser.user.lastName}`,
  }))

type Props = {
  open: boolean
  closeModal: () => void
  form: FormInstance
  onSubmit: (values: FormFieldValues) => void
  isLoading?: boolean
  initialValues?: Partial<FormFieldValues>
  title?: React.ReactNode
  footer?: React.ReactNode
  pendingCount?: number
  contextualDate?: string
}

export const DefectModal: React.FC<Props> = ({
  open,
  closeModal,
  form,
  onSubmit,
  initialValues,
  title,
  footer,
  isLoading,
  pendingCount,
  contextualDate,
}) => {
  const { t } = useLocale()
  const [isVoiceGenerating, setIsVoiceGenerating] = useState(false)
  const { setAIRunning } = useAIActivity()
  const { aiModule } = useEnabledModules()
  const { registerUsage } = useRegisterAIToolUsage('aiDefects')

  const router = useRouter()

  const {
    aiEnabled,
    aiFeatures: { defectEnhancement },
    aiOptedIn,
  } = aiModule
  const defectEnhancementActive = aiOptedIn && defectEnhancement

  const setAIActivity = (running: boolean) => {
    setAIRunning(running)
    setIsVoiceGenerating(running)
  }

  const { project, isProjectSelected } = useProject()
  const modalContentRef = useRef<HTMLDivElement>(null)

  const { data: settingsData } = useQuery<
    DefectSettingsUiQuery,
    DefectSettingsUiQueryVariables
  >(GET_DEFECT_SETTINGS, {
    fetchPolicy: 'cache-and-network',
    skip: !open,
  })

  const showNotifyOnCreateAlert =
    settingsData?.defectSettings?.notifyAssigneesOnDefectCreated &&
    isEmpty(form.getFieldValue(Field.ID))

  const { data: locationsData } = useQuery<
    ProjectLocationsUiQuery,
    ProjectLocationsUiQueryVariables
  >(GET_PROJECT_LOCATIONS, {
    variables: { projectId: project?.id },
    skip: !defectEnhancementActive || !project?.id || !open,
  })

  // This fetches workers attached to project to reduce results returned
  //  Slightly different to the assignee modal which lets you see every worker
  //  Consider an improvement where we search for a user with AI in the DB instead
  const { data: allAssigneesData } = useQuery<
    DefectAssigneesQuery,
    DefectAssigneesQueryVariables
  >(GET_ALL_ASSIGNEES, {
    variables: {
      projectId: project?.id,
      filter: {
        includeBuilderSubcontractor: true,
      },
    },
    skip: !defectEnhancementActive || !project?.id || !open,
  })

  const allAssignees = useMemo(() => {
    if (allAssigneesData) {
      return [
        ...mapWorkers(allAssigneesData.workersByProject),
        ...mapSubcontractors(allAssigneesData.subcontractorsByProject),
        ...mapAdminUsers(allAssigneesData.adminUsers),
      ]
    }
    return []
  }, [allAssigneesData])

  const preSubmit = async (values: FormFieldValues) => {
    const stillUploading = values.attachments?.some(
      (a) => a.status === 'uploading'
    )

    if (stillUploading) {
      message.warning({
        content: UPLOAD_WARNING,
        getPopupContainer: () => modalContentRef.current,
      })
      return
    }

    onSubmit(values)
  }

  const handleGenerateFromVoice = async (voiceInput: string) => {
    if (!defectEnhancementActive) {
      return
    }

    try {
      setAIActivity(true)
      message.info('Generating defect from voice input...')

      registerUsage(
        `generate defect from voice`,
        voiceInput ?? 'No audio captured'
      )

      if (!voiceInput) {
        message.error(
          'Unable to capture audio. Please ensure you have a microphone enabled.'
        )
        return
      }

      const currentTranscriptions =
        form.getFieldValue(Field.TRANSCRIPTIONS) ?? []
      const newTranscriptions = [...currentTranscriptions, voiceInput].join(
        'END OF TRANSCRIPTION \n START OF TRANSCRIPTION \n'
      )
      form.setFieldsValue({ [Field.TRANSCRIPTIONS]: newTranscriptions })

      const response = await fetch('/api/ai/voiceDefects', {
        method: 'POST',
        body: JSON.stringify({
          locations: locationsData,
          currentData: form.getFieldsValue(),
        }),
      })

      const data = (await response.json()) as VoiceDefectHandlerResponse

      if ('error' in data) {
        throw new Error(data.error)
      }
      if (data.isIrrelevantOrNotUnderstandable) {
        message.error('Unable to understand your input. Please try again.')
        return
      }

      const {
        description,
        title,
        priority,
        dueDate,
        locationId,
        status,
        type,
        shouldAssign,
      } = data

      if (title) {
        form.setFieldsValue({ [Field.TITLE]: title })
      }
      if (description) {
        form.setFieldsValue({ [Field.DESCRIPTION]: description })
      }
      if (priority) {
        form.setFieldsValue({ [Field.PRIORITY]: priority })
      }
      if (dueDate) {
        const parsedDate = moment(dueDate)
        if (parsedDate.isValid()) {
          form.setFieldsValue({ [Field.DUE_DATE]: parsedDate })
        }
      }
      if (locationId) {
        if (isValidLocation(locationsData.projectLocations, locationId)) {
          form.setFieldsValue({ [Field.LOCATION]: locationId })
        }
      }
      if (status) {
        form.setFieldsValue({ [Field.STATUS]: status })
      }
      if (type) {
        form.setFieldsValue({ [Field.TYPE]: type })
      }

      if (shouldAssign) {
        const assigneeData = await getAISelectAssignees({
          allAssignees,
          currentAssignees: form.getFieldValue(Field.ASSIGNEES),
          transcriptions: newTranscriptions,
        })

        if ('error' in assigneeData) {
          throw new Error(assigneeData.error)
        }

        if (assigneeData.assignees) {
          form.setFieldsValue({ [Field.ASSIGNEES]: assigneeData.assignees })
        }
      }
    } catch (e) {
      Sentry.captureException(e)
      message.error('Failed to generate defect from voice input')
    } finally {
      setAIActivity(false)
    }
  }

  return (
    <Modal
      centered
      noPadding
      width={buildPassTheme.modal.medium}
      open={open}
      onCancel={closeModal}
      maskClosable={false}
      title={title}
      footer={footer}
      modalRender={(children) =>
        isLoading ? (
          children
        ) : (
          <Form form={form} onFinish={preSubmit} initialValues={initialValues}>
            {children}
          </Form>
        )
      }
    >
      {pendingCount && (
        <PendingBar>
          <IconStack size={16} /> {pluralise(pendingCount, 'Defect')} pending...
        </PendingBar>
      )}
      <Content ref={modalContentRef}>
        {isLoading ? (
          <LoadingWrapper>
            <DozerLoading loading />
          </LoadingWrapper>
        ) : (
          <>
            {showNotifyOnCreateAlert && (
              <Alert
                bordered
                variant="warning"
                icon={<IconAlertTriangle size={18} />}
                title={
                  <Text>
                    Assignees will be notified immediately when a defect is
                    created. Change{' '}
                    <Link href={routes.settings.defects()}>settings</Link>.
                  </Text>
                }
                style={{ marginTop: '0.5rem' }}
              />
            )}
            <Form.Item noStyle name={Field.ID}>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item noStyle name={Field.CONTINUE}>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item noStyle name={Field.TRANSCRIPTIONS}>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item dependencies={[Field.PROJECT_ID]} noStyle>
              {({ getFieldValue }) => (
                <Form.Item
                  noStyle
                  name={Field.ATTACHMENTS}
                  valuePropName="fileList"
                >
                  <Uploader
                    projectId={getFieldValue(Field.PROJECT_ID)}
                    attachmentType={AttachmentType.Media}
                    attachmentResource={Resource.Defect}
                    attachmentSortDate={contextualDate}
                    enableMarkup
                  />
                </Form.Item>
              )}
            </Form.Item>
            {aiEnabled ? (
              defectEnhancementActive ? (
                <AudioTranscriber
                  title={
                    isVoiceGenerating
                      ? 'Generating defect...'
                      : 'Generate from voice'
                  }
                  helpText="Give details for each field"
                  onFinalText={handleGenerateFromVoice}
                  onFinishInput={() => {
                    setAIActivity(true)
                  }}
                />
              ) : (
                <Popconfirm
                  title="To enable this feature you need to enable AI Tools on your account via settings."
                  onConfirm={() => router.push(routes.settings.aiTools())}
                  okText="Take me to settings"
                  cancelText="No"
                >
                  <div style={{ width: 'fit-content' }}>
                    <AudioTranscriber title="Generate from voice" disabled />
                  </div>
                </Popconfirm>
              )
            ) : null}
            <Form.Item noStyle name={Field.TITLE}>
              <TitleInput placeholder="Defect title" autoSize />
            </Form.Item>
            <Form.Item noStyle name={Field.DESCRIPTION}>
              <DescriptionInput placeholder="Add description..." autoSize />
            </Form.Item>
            <Buttons>
              <Form.Item noStyle name={Field.TYPE}>
                <KeyValueDropdown
                  options={Object.entries(DEFECT_TYPES).reduce(
                    (acc, [key, { labelPath, icon }]) => ({
                      ...acc,
                      [key]: {
                        label: t(labelPath),
                        icon,
                      },
                    }),
                    {}
                  )}
                />
              </Form.Item>

              <Form.Item noStyle name={Field.STATUS}>
                <KeyValueDropdown
                  options={Object.entries(DEFECT_STATUSES).reduce(
                    (acc, [key, { labelPath, icon }]) => ({
                      ...acc,
                      [key]: {
                        label: t(labelPath),
                        icon,
                      },
                    }),
                    {}
                  )}
                />
              </Form.Item>

              <Form.Item dependencies={[Field.PROJECT_ID]} noStyle>
                {({ getFieldValue }) => (
                  <Form.Item noStyle name={Field.LOCATION}>
                    <ProjectLocationDropdown
                      projectId={getFieldValue(Field.PROJECT_ID)}
                    />
                  </Form.Item>
                )}
              </Form.Item>

              <Form.Item noStyle name={Field.PRIORITY}>
                <KeyValueDropdown
                  options={Object.entries(DEFECT_PRIORITIES).reduce(
                    (acc, [key, { labelPath, icon }]) => ({
                      ...acc,
                      [key]: {
                        label: t(labelPath),
                        icon,
                      },
                    }),
                    {}
                  )}
                  clear={{
                    label: 'No priority',
                    icon: <IconDots size={16} />,
                  }}
                />
              </Form.Item>

              <Form.Item noStyle name={Field.DUE_DATE}>
                <DateDropdown noValueLabel="Due date" />
              </Form.Item>

              <Form.Item noStyle name={Field.ASSIGNEES}>
                <AssigneeSelect
                  project={isProjectSelected ? project : undefined}
                  includeBuilderSubcontractor={false}
                  showWorkers={
                    settingsData?.defectSettings?.allowWorkerAssignees ?? false
                  }
                >
                  {(assignees, openModal, onClear) => (
                    <AssigneesWrapper>
                      <BaseButton
                        onClick={openModal}
                        hasValue={false}
                        icon={<IconUsers size={16} />}
                      >
                        {assignees?.length ? '+' : 'Add assignees'}
                      </BaseButton>

                      <Assignees>
                        {assignees?.map((assignee) => (
                          <AssigneePill
                            key={assignee.id}
                            id={assignee.id}
                            name={assignee.name}
                            type={assignee.type}
                            onClear={onClear}
                          />
                        ))}
                      </Assignees>
                    </AssigneesWrapper>
                  )}
                </AssigneeSelect>
              </Form.Item>
            </Buttons>
          </>
        )}
      </Content>
    </Modal>
  )
}
